import { Form } from "react-bootstrap";
import TextField from "@components/FormsV2/TextField/TextField";
import GenericToggle from "@components/FormsV2/GenericToggle/GenericToggle";
import { SelectOptionsEditor } from "./SelectOptionsEditor";
import {
  VALUE_TYPE_OPTIONS,
  STEP_OPTIONS,
  DATE_MIN_OPTIONS,
  DATE_MAX_OPTIONS,
  hasMinMax,
  hasStep,
  hasSelectOptions,
  type QuestionFormValues,
  type ValueType,
  type QuestionSettings,
  type SelectOption,
} from "./types";
import "./style.css";

export interface QuestionFormProps {
  values: QuestionFormValues;
  onChange: (next: QuestionFormValues) => void;
  mode: "create" | "edit";
  slug: string;
}

export const QuestionForm = ({
  values,
  onChange,
  mode,
  slug,
}: QuestionFormProps): JSX.Element => {
  const set = <K extends keyof QuestionFormValues>(
    key: K,
    v: QuestionFormValues[K]
  ) => onChange({ ...values, [key]: v });

  const setSetting = <K extends keyof QuestionSettings>(
    key: K,
    v: QuestionSettings[K]
  ) => onChange({ ...values, settings: { ...values.settings, [key]: v } });

  const vt = values.valueType;
  const minMaxLabel =
    vt === "number" ? "Value" : vt === "date" ? "Date" : "Length";

  return (
    <Form className="question-form">
      <Form.Group className="mb-3">
        <Form.Label htmlFor="q-question">Question</Form.Label>
        <TextField
          label={null}
          id="q-question"
          value={values.question}
          onChange={(e) => set("question", e.target.value)}
          required
        />
      </Form.Group>

      <div className="row">
        <div className="col-sm-6">
          <Form.Group className="mb-3">
            <Form.Label htmlFor="q-name">Name</Form.Label>
            <TextField
              label={null}
              id="q-name"
              value={values.name}
              onChange={(e) => set("name", e.target.value)}
              required
            />
            <small className="text-muted d-block">
              Used when showing answers.
            </small>
          </Form.Group>
        </div>
        {mode === "edit" && (
          <div className="col-sm-6">
            <Form.Group className="mb-3">
              <Form.Label htmlFor="q-slug">Slug</Form.Label>
              <TextField
                label={null}
                id="q-slug"
                value={slug}
                onChange={() => undefined}
                readOnly
                disabled
              />
              <small className="text-muted d-block">
                Used in calculations and lookups.
              </small>
            </Form.Group>
          </div>
        )}
      </div>

      <div className="row">
        <div className="col-sm-6">
          <Form.Group className="mb-3">
            <Form.Label htmlFor="q-value-type">Type of Value</Form.Label>
            <Form.Select
              id="q-value-type"
              aria-label="Type of Value"
              value={vt}
              disabled={mode === "edit"}
              onChange={(e) => set("valueType", e.target.value as ValueType)}
            >
              {VALUE_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
        <div className="col-sm-6">
          <Form.Label>Required?</Form.Label>
          <div>
            <GenericToggle
              key={`required-${mode}`}
              initialValueOfIsOn={values.required}
              onToggle={(on: boolean) => set("required", on)}
              onText="Required"
              offText="Optional"
              hasText
            />
          </div>
        </div>
      </div>

      {hasMinMax(vt) && vt !== "date" && (
        <div className="row">
          <div className="col-sm-4">
            <Form.Group className="mb-3">
              <Form.Label htmlFor="q-min">{`Minimum ${minMaxLabel}`}</Form.Label>
              <Form.Control
                id="q-min"
                type="number"
                value={values.settings.min}
                onChange={(e) => setSetting("min", e.target.value)}
              />
            </Form.Group>
          </div>
          <div className="col-sm-4">
            <Form.Group className="mb-3">
              <Form.Label htmlFor="q-max">{`Maximum ${minMaxLabel}`}</Form.Label>
              <Form.Control
                id="q-max"
                type="number"
                value={values.settings.max}
                onChange={(e) => setSetting("max", e.target.value)}
              />
            </Form.Group>
          </div>
          {hasStep(vt) && (
            <div className="col-sm-4">
              <Form.Group className="mb-3">
                <Form.Label htmlFor="q-step">Decimal Places</Form.Label>
                <Form.Select
                  id="q-step"
                  aria-label="Decimal Places"
                  value={values.settings.step}
                  onChange={(e) =>
                    setSetting(
                      "step",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                >
                  {STEP_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
          )}
        </div>
      )}

      {vt === "date" && (
        <div className="row">
          <div className="col-sm-6">
            <Form.Group className="mb-3">
              <Form.Label htmlFor="q-date-min">Minimum Date</Form.Label>
              <Form.Select
                id="q-date-min"
                aria-label="Minimum Date"
                value={values.settings.min}
                onChange={(e) => setSetting("min", e.target.value)}
              >
                {DATE_MIN_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="col-sm-6">
            <Form.Group className="mb-3">
              <Form.Label htmlFor="q-date-max">Maximum Date</Form.Label>
              <Form.Select
                id="q-date-max"
                aria-label="Maximum Date"
                value={values.settings.max}
                onChange={(e) => setSetting("max", e.target.value)}
              >
                {DATE_MAX_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
        </div>
      )}

      {hasSelectOptions(vt) && (
        <SelectOptionsEditor
          value={values.selectOptions}
          onChange={(next: SelectOption[]) => set("selectOptions", next)}
        />
      )}
    </Form>
  );
};

export default QuestionForm;
